import { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";

const useTrackerData = (childId) => {
  const [feedingsData, setFeedingsData] = useState([]);
  const [diapersData, setDiapersData] = useState([]);
  const [sleepsData, setSleepsData] = useState([]);
  const [extractedMilkData, setExtractedMilkData] = useState([]);
  const [weightsData, setWeightsData] = useState([]);
  const [, setFeedingPlanData] = useState([]);
  const [tummyTimesData, setTummyTimesData] = useState([]);
  const [medicinesData, setMedicinesData] = useState([]);

  const [log, setLog] = useState([]);
  const [weightLog, setWeightLog] = useState([]);

  const [activeFeeding, setActiveFeeding] = useState(null);
  const [activeSleep, setActiveSleep] = useState(null);
  const [activeTummyTime, setActiveTummyTime] = useState(null);

  const [lastSleep, setLastSleep] = useState(null);
  const [lastFeeding, setLastFeeding] = useState(null);

  // Force re-render every second for active session timers
  const [timer, setTimer] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setTimer(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Main data subscriptions
  useEffect(() => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 6);
    oneWeekAgo.setHours(0, 0, 0, 0);

    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    oneMonthAgo.setHours(0, 0, 0, 0);

    const queries = [
      {
        ref: query(
          collection(db, `children/${childId}/feedings`),
          where("start", ">=", oneMonthAgo.toISOString())
        ),
        setter: setFeedingsData,
        type: "Feeding",
        icon: "🍼",
        background: "#AED581",
      },
      {
        ref: query(
          collection(db, `children/${childId}/diapers`),
          where("start", ">=", oneWeekAgo.toISOString())
        ),
        setter: setDiapersData,
        type: "Diaper",
        icon: "💩",
        background: "#FCD0BA",
      },
      {
        ref: query(
          collection(db, `children/${childId}/sleeps`),
          where("start", ">=", oneMonthAgo.toISOString())
        ),
        setter: setSleepsData,
        type: "Sleep",
        icon: "😴",
        background: "#FEFAD4",
      },
      {
        ref: query(
          collection(db, `children/${childId}/extractedMilk`),
          where("start", ">=", oneWeekAgo.toISOString())
        ),
        setter: setExtractedMilkData,
        type: "Extracted Milk",
        icon: "🥛",
        background: "#8FB9A8",
      },
      {
        ref: query(
          collection(db, `children/${childId}/tummyTimes`),
          where("start", ">=", oneWeekAgo.toISOString())
        ),
        setter: setTummyTimesData,
        type: "Tummy Time",
        icon: "🧸",
        background: "#FFECB3",
      },
      {
        ref: query(
          collection(db, `children/${childId}/medicines`),
          where("start", ">=", oneWeekAgo.toISOString())
        ),
        setter: setMedicinesData,
        type: "Medicine",
        icon: "💊",
        background: "#E8D5F2",
      },
    ];

    const unsubscribers = queries.map(
      ({ ref, setter, type, icon, background }) =>
        onSnapshot(ref, (snapshot) => {
          setter(
            snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
              type,
              icon,
              background,
            }))
          );
        })
    );

    const unsubPlans = onSnapshot(
      collection(db, `children/${childId}/feedingPlans`),
      (snapshot) => {
        setFeedingPlanData(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            type: "Feeding Plan",
            icon: "📅",
            background: "#FCE2B8",
          }))
        );
      }
    );

    return () => {
      unsubscribers.forEach((unsub) => unsub());
      unsubPlans();
    };
  }, [childId]);

  // Weights subscription (ordered)
  useEffect(() => {
    const q = query(
      collection(db, `children/${childId}/weights`),
      orderBy("start", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setWeightsData(
        snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          type: "Weight",
          icon: "⚖️",
          background: "#765D69",
        }))
      );
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  // Active sessions subscriptions
  useEffect(() => {
    const listeners = [
      { col: `children/${childId}/activeFeeding`, setter: setActiveFeeding },
      { col: `children/${childId}/activeSleep`, setter: setActiveSleep },
      {
        col: `children/${childId}/activeTummyTime`,
        setter: setActiveTummyTime,
      },
    ];

    const unsubscribers = listeners.map(({ col, setter }) =>
      onSnapshot(collection(db, col), (snapshot) => {
        if (!snapshot.empty) {
          const data = snapshot.docs[0].data();
          setter({ ...data, id: snapshot.docs[0].id, active: true });
        } else {
          setter(null);
        }
      })
    );

    return () => unsubscribers.forEach((unsub) => unsub());
  }, [childId]);

  // Combine all data into a sorted log
  useEffect(() => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 6);
    oneWeekAgo.setHours(0, 0, 0, 0);
    const oneWeekAgoISO = oneWeekAgo.toISOString();

    const filteredSleeps = sleepsData.filter((s) => s.start >= oneWeekAgoISO);
    const filteredFeedings = feedingsData.filter(
      (f) => f.start >= oneWeekAgoISO
    );

    const combined = [
      ...filteredFeedings,
      ...diapersData,
      ...filteredSleeps,
      ...extractedMilkData,
      ...tummyTimesData,
      ...medicinesData,
    ];

    if (activeFeeding)
      combined.push({
        ...activeFeeding,
        type: "Feeding",
        icon: "🍼",
        background: "#AED581",
      });
    if (activeSleep)
      combined.push({
        ...activeSleep,
        type: "Sleep",
        icon: "😴",
        background: "#FEFAD4",
      });
    if (activeTummyTime)
      combined.push({
        ...activeTummyTime,
        type: "Tummy Time",
        icon: "🧸",
        background: "#FFECB3",
      });

    combined.sort(
      (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()
    );
    setLog(combined);

    setLastSleep(
      [...sleepsData].sort(
        (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()
      )[0]
    );
    setLastFeeding(
      [...feedingsData].sort(
        (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()
      )[0]
    );
  }, [
    feedingsData,
    diapersData,
    sleepsData,
    extractedMilkData,
    tummyTimesData,
    medicinesData,
    activeFeeding,
    activeSleep,
    activeTummyTime,
    timer, // re-render every second for active session timers
  ]);

  // Sort weight log
  useEffect(() => {
    setWeightLog(
      [...weightsData].sort(
        (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()
      )
    );
  }, [weightsData]);

  return {
    feedingsData,
    sleepsData,
    log,
    weightLog,
    activeFeeding,
    setActiveFeeding,
    activeSleep,
    setActiveSleep,
    activeTummyTime,
    setActiveTummyTime,
    lastSleep,
    lastFeeding,
  };
};

export default useTrackerData;
